import { useEffect, useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  MenuItem,
  Typography,
  Autocomplete,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarCustodiosPPE = () => {
  const navigate = useNavigate()
  const { state } = useLocation() // Recibimos la fila seleccionada desde la grilla
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Rescatamos el código original para permitir cambios de ID en tipos "Otros"
  const [emcodempViejo, setEmcodempViejo] = useState("")

  // Estado del formulario
  const [formData, setFormData] = useState({
    emcodemp: "",
    cusnombre: "",
    cusdirec: "",
    custipo: "E",
    cusstatus: "A",
  })

  useEffect(() => {
    // Precargar los inputs con los valores de la fila enviada desde la grilla
    if (state) {
      setFormData({
        emcodemp: state.emcodemp || "",
        cusnombre: state.cusnombre || "",
        cusdirec: state.cusdirec || "",
        custipo: state.custipo || "E",
        cusstatus: state.cusstatus || "A",
      })
      setEmcodempViejo(state.emcodemp || "")
    } else {
      navigate(-1)
    }
  }, [state, navigate])

  // --- LÓGICA PARA CARGAR EMPLEADOS (Solo si el tipo es Empleado) ---
  const { data: rawEmpleados, isLoading: isLoadingEmpleados } = useQuery({
    queryKey: ["listaEmpleados_Editar"],
    queryFn: async () => {
      try {
        const response = await api.get("/CustodiosPPE/getListaEmpleados")
        return response?.data?.data || response?.data || response || []
      } catch (error) {
        console.error("Error al cargar Empleados:", error)
        return []
      }
    },
    enabled: formData.custipo === "E",
  })

  const empleadosValidados = Array.isArray(rawEmpleados) ? rawEmpleados : []
  const listaEmpleados = empleadosValidados.map((item) => ({
    id: item.emcodemp,
    label: `${item.emcodemp} - ${item.cusnombre}`,
  }))

  const {
    mutateAsync: SaveEdicionCustodio,
    isPending: isSavingEdicion,
    isError,
  } = useMutation({
    queryKey: ["isEditingCustodio"],
    fn: async (data) => {
      const response = await api.post("/CustodiosPPE/updateCustodiosPPE", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  const handleInputChange = (field, value) => {
    let finalValue = value

    if (field === "custipo") {
      // Si cambia el tipo, limpiamos los datos vinculados al código
      setFormData((prev) => ({ ...prev, custipo: value, emcodemp: "", cusnombre: "" }))
      return
    }

    if (field !== "cusstatus") finalValue = value.toUpperCase()
    if (field === "emcodemp") finalValue = finalValue.slice(0, 10)
    if (field === "cusnombre") finalValue = finalValue.slice(0, 100)
    if (field === "cusdirec") finalValue = finalValue.slice(0, 120)

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    const payload = {
      emcodempOld: emcodempViejo,
      emcodempNew: formData.emcodemp,
      cusnombre: formData.cusnombre,
      cusdirec: formData.cusdirec,
      custipo: formData.custipo,
      cusstatus: formData.cusstatus,
    }

    try {
      if (!payload.emcodempNew.trim()) return showWarning("El Código del Custodio es obligatorio")
      if (!payload.cusnombre.trim()) return showWarning("El Nombre del Custodio es obligatorio")

      await SaveEdicionCustodio(payload)
    } catch (error) {
      console.error("Error al editar el Custodio:", error)
    }
  }

  const actualizarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (action) =>
      action?.acccaption === "ACTUALIZAR" ||
      action?.acccaption === "GUARDAR" ||
      action?.acccaption === "EDITAR" ||
      action?.acccaption === "EJECUTAR",
  )

  const toolbarActions = actualizarAction
    ? [
        {
          label: actualizarAction.acccaption,
          key: actualizarAction.acccaption,
          icon: getIconComponent(actualizarAction.accnameicono, actualizarAction.acctipoico),
        },
      ]
    : [{ label: "Actualizar", key: "ACTUALIZAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingEdicion || isLoadingEmpleados}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mr: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Editar Custodio de PPE: {emcodempViejo}</b>
        </div>

        <CustomBackdrop isLoading={isSavingEdicion || isLoadingEmpleados} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos del Custodio
            </Typography>

            <Grid container spacing={3}>
              {/* TIPO DE CUSTODIO */}
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: "bold" }}>
                    Tipo de Custodio *
                  </FormLabel>
                  <RadioGroup
                    row
                    value={formData.custipo}
                    onChange={(e) => handleInputChange("custipo", e.target.value)}
                  >
                    <FormControlLabel value="E" control={<Radio />} label="Empleado" />
                    <FormControlLabel value="O" control={<Radio />} label="Otros" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* CÓDIGO / AUTOCOMPLETE */}
              <Grid item xs={12} sm={4}>
                {formData.custipo === "E" ? (
                  <Autocomplete
                    options={listaEmpleados}
                    getOptionLabel={(option) => option.label || ""}
                    value={
                      listaEmpleados.find((emp) => emp.id === formData.emcodemp) ||
                      (formData.emcodemp
                        ? { id: formData.emcodemp, label: `${formData.emcodemp} - ${formData.cusnombre}` }
                        : null)
                    }
                    onChange={(event, newValue) => {
                      if (newValue) {
                        const nombreExtraido = newValue.label.split(" - ")[1]
                        setFormData((prev) => ({ ...prev, emcodemp: newValue.id, cusnombre: nombreExtraido }))
                      } else {
                        setFormData((prev) => ({ ...prev, emcodemp: "", cusnombre: "" }))
                      }
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Empleado *"
                        InputLabelProps={{ shrink: true }}
                        error={isError && !formData.emcodemp}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    label="Código *"
                    value={formData.emcodemp}
                    onChange={(e) => handleInputChange("emcodemp", e.target.value)}
                    error={isError && !formData.emcodemp}
                    inputProps={{ maxLength: 10 }}
                  />
                )}
              </Grid>

              {/* NOMBRE */}
              <Grid item xs={12} sm={8}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Nombre Completo *"
                  value={formData.cusnombre}
                  onChange={(e) => handleInputChange("cusnombre", e.target.value)}
                  disabled={formData.custipo === "E"}
                  error={isError && !formData.cusnombre}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              {/* DIRECCIÓN */}
              <Grid item xs={12} sm={9}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Dirección"
                  value={formData.cusdirec}
                  onChange={(e) => handleInputChange("cusdirec", e.target.value)}
                  inputProps={{ maxLength: 120 }}
                />
              </Grid>

              {/* ESTADO */}
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Estado"
                  value={formData.cusstatus}
                  onChange={(e) => handleInputChange("cusstatus", e.target.value)}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EditarCustodiosPPE
